import { useEffect, useState } from 'react'
import { Alert, Button, Card, Form, Input, Space, Switch, message } from 'antd'
import { adminApi } from '../../api'
import { PageHeader } from '../../components/ui'

type Neo4jHealth = {
  configured: boolean
  available: boolean
  error?: string
}

type Neo4jConfig = {
  enabled: boolean
  uri: string
  username: string
  password: string
  password_set: boolean
  database: string
  source: 'database' | 'environment'
  health: Neo4jHealth
}

type Neo4jFormValues = Pick<Neo4jConfig, 'enabled' | 'uri' | 'username' | 'password' | 'database'>

export default function Neo4jSettings() {
  const [form] = Form.useForm<Neo4jFormValues>()
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [config, setConfig] = useState<Neo4jConfig | null>(null)
  const enabled = Form.useWatch('enabled', form) ?? false

  const loadConfig = () =>
    adminApi.get<Neo4jConfig>('/admin/settings/neo4j').then((response) => {
      const next = response.data
      setConfig(next)
      form.setFieldsValue({
        enabled: next.enabled,
        uri: next.uri,
        username: next.username || 'neo4j',
        password: '',
        database: next.database || 'neo4j',
      })
    })

  useEffect(() => {
    loadConfig().catch((error) => message.error(error.message))
  }, [form])

  const formPayload = (values: Neo4jFormValues) => {
    const payload: Partial<Neo4jFormValues> = { ...values }
    if (!payload.password) delete payload.password
    return payload
  }

  const save = () => {
    void form.validateFields().then((values) => {
      setLoading(true)
      adminApi
        .put('/admin/settings/neo4j', formPayload(values))
        .then(() => {
          message.success('Neo4j 配置已保存并生效')
          return loadConfig()
        })
        .catch((error) => message.error(error.message))
        .finally(() => setLoading(false))
    })
  }

  const testConnection = () => {
    void form.validateFields().then((values) => {
      setTesting(true)
      adminApi
        .post<{ ok: boolean; message: string }>('/admin/settings/neo4j/test', formPayload(values))
        .then((response) => message.success(response.data.message || 'Neo4j 连接成功'))
        .catch((error) => message.error(error.message))
        .finally(() => setTesting(false))
    })
  }

  const status = !enabled
    ? { type: 'info' as const, title: 'Neo4j 当前未启用' }
    : config?.health.available
      ? { type: 'success' as const, title: 'Neo4j 连接正常' }
      : { type: 'warning' as const, title: '尚未确认当前表单配置可用' }

  return (
    <div>
      <PageHeader
        title="Neo4j 配置"
        description="统一维护知识图谱使用的 Neo4j 连接。保存后新请求会自动使用最新配置，无需重启应用。"
      />

      <Card>
        <Form form={form} layout="vertical" style={{ maxWidth: 720 }}>
          <Alert
            showIcon
            type={status.type}
            message={status.title}
            description={
              config?.source === 'environment'
                ? '当前配置来自服务器环境变量；首次保存后将切换为后台配置。'
                : '当前配置由管理后台维护。密码不会在页面或查询接口中回显。'
            }
            style={{ marginBottom: 20 }}
          />

          <Form.Item name="enabled" label="启用 Neo4j" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            name="uri"
            label="连接地址"
            rules={enabled ? [{ required: true, message: '请填写 Neo4j 连接地址' }] : []}
            extra="支持 neo4j://、neo4j+s://、bolt:// 等 Neo4j 官方协议；账号和密码请单独填写。"
          >
            <Input placeholder="bolt://127.0.0.1:7687" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="username"
            label="用户名"
            rules={enabled ? [{ required: true, message: '请填写用户名' }] : []}
          >
            <Input placeholder="neo4j" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={
              enabled && !config?.password_set
                ? [{ required: true, message: '请填写 Neo4j 密码' }]
                : []
            }
            extra={config?.password_set ? '密码已配置；留空保存或测试将继续使用现有密码。' : undefined}
          >
            <Input.Password placeholder={config?.password_set ? '已配置' : '请输入密码'} autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="database"
            label="数据库名称"
            rules={enabled ? [{ required: true, message: '请填写数据库名称' }] : []}
            extra="通常为 neo4j；填写目标数据库名称，连接测试会确认该数据库可访问。"
          >
            <Input placeholder="neo4j" autoComplete="off" />
          </Form.Item>

          <Space>
            <Button type="primary" loading={loading} onClick={save}>
              保存配置
            </Button>
            <Button disabled={!enabled} loading={testing} onClick={testConnection}>
              测试连接
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  )
}
